import { useCallback, useEffect, useRef, useState } from "react";

interface DeletionState {
    timeoutId: NodeJS.Timeout;
    intervalId: NodeJS.Timeout;
    secondsRemaining: number;
}

interface UseCommentDeletionReturn {
    pendingDeletions: Map<string, DeletionState>;
    scheduleDelete: (commentId: string) => void;
    revertDelete: (commentId: string) => void;
    isPending: (commentId: string) => boolean;
    getSecondsRemaining: (commentId: string) => number | null;
}

/**
 * 댓글 삭제 카운트다운 관리 훅
 * 5초 카운트다운과 취소 기능 제공
 */
export const useCommentDeletion = (
    onDeleteComment: (commentId: string) => Promise<void>,
    countdownSeconds: number = 5
): UseCommentDeletionReturn => {
    const [pendingDeletions, setPendingDeletions] = useState<
        Map<string, DeletionState>
    >(new Map());
    const pendingDeletionsRef = useRef(pendingDeletions);

    useEffect(() => {
        pendingDeletionsRef.current = pendingDeletions;
    }, [pendingDeletions]);

    useEffect(() => {
        return () => {
            pendingDeletionsRef.current.forEach((deletion) => {
                clearTimeout(deletion.timeoutId);
                clearInterval(deletion.intervalId);
            });
        };
    }, []);

    const scheduleDelete = useCallback(
        (commentId: string) => {
            if (pendingDeletions.has(commentId)) return;

            let secondsRemaining = countdownSeconds;

            const intervalId = setInterval(() => {
                setPendingDeletions((prev) => {
                    const deletion = prev.get(commentId);
                    if (!deletion) return prev;

                    const newMap = new Map(prev);
                    newMap.set(commentId, {
                        ...deletion,
                        secondsRemaining: deletion.secondsRemaining - 1
                    });
                    return newMap;
                });
            }, 1000);

            const timeoutId = setTimeout(async () => {
                clearInterval(intervalId);

                setPendingDeletions((prev) => {
                    const newMap = new Map(prev);
                    newMap.delete(commentId);
                    return newMap;
                });

                try {
                    await onDeleteComment(commentId);
                } catch (error) {
                    console.error("Failed to delete comment:", error);
                    alert("댓글 삭제에 실패했습니다. 다시 시도해주세요.");
                }
            }, countdownSeconds * 1000);

            setPendingDeletions((prev) => {
                const newMap = new Map(prev);
                newMap.set(commentId, {
                    timeoutId,
                    intervalId,
                    secondsRemaining
                });
                return newMap;
            });
        },
        [countdownSeconds, onDeleteComment, pendingDeletions]
    );

    const revertDelete = useCallback(
        (commentId: string) => {
            const deletion = pendingDeletions.get(commentId);
            if (!deletion) return;

            clearTimeout(deletion.timeoutId);
            clearInterval(deletion.intervalId);

            setPendingDeletions((prev) => {
                const newMap = new Map(prev);
                newMap.delete(commentId);
                return newMap;
            });
        },
        [pendingDeletions]
    );

    const isPending = useCallback(
        (commentId: string) => pendingDeletions.has(commentId),
        [pendingDeletions]
    );

    const getSecondsRemaining = useCallback(
        (commentId: string) =>
            pendingDeletions.get(commentId)?.secondsRemaining ?? null,
        [pendingDeletions]
    );

    return {
        pendingDeletions,
        scheduleDelete,
        revertDelete,
        isPending,
        getSecondsRemaining
    };
};
